import React from 'react';
import {
    ActivityItem,
    DefaultButton,
    Dialog,
    DialogType,
    Icon,
    IconButton,
    Link,
    Spinner,
    SpinnerSize,
    Stack,
    useTheme,
} from '@fluentui/react';
import PanelWrapper from '../../Common/PanelWrapper';
import { useGetEmailLogQuery } from '../../../store/endpoints/StorefrontSettingsEndpoints';
import { IEmailActivity } from '../../../models/Storefront';

interface IEmailLogsPanelProps {
    isOpen: boolean;
    onDismiss: () => void;
}

const STATUS_META: Record<string, { icon: string; label: string; ok: boolean }> = {
    sent: { icon: 'CompletedSolid', label: 'Sent', ok: true },
    fellBack: { icon: 'WarningSolid', label: 'Sent via fallback', ok: false },
    failed: { icon: 'StatusErrorFull', label: 'Failed', ok: false },
};

/** Slide-out panel listing recent outbound email send attempts as an activity log, mirroring the webhook/extension log UIs. */
const EmailLogsPanel: React.FC<IEmailLogsPanelProps> = ({ isOpen, onDismiss }) => {
    const theme = useTheme();
    const { data, isFetching, refetch } = useGetEmailLogQuery({ count: 100 }, { skip: !isOpen });
    const [viewing, setViewing] = React.useState<IEmailActivity | undefined>();

    return (
        <PanelWrapper isOpen={isOpen} onDismiss={onDismiss} title='Email Log'>
            <Stack tokens={{ childrenGap: '1em' }}>
                <Stack horizontal horizontalAlign='end'>
                    <IconButton iconProps={{ iconName: 'Refresh' }} title='Refresh' disabled={isFetching} onClick={refetch} />
                </Stack>

                {isFetching ? (
                    <Spinner label='Loading email log...' size={SpinnerSize.large} style={{ marginTop: '2em' }} />
                ) : !data?.length ? (
                    <p style={{ color: theme.palette.neutralSecondary }}>No emails have been sent yet.</p>
                ) : (
                    <Stack tokens={{ childrenGap: '1em' }}>
                        {data.map((activity, i) => (
                            <EmailActivityRow key={activity.id ?? `${activity.timestamp}-${i}`} activity={activity} onView={() => setViewing(activity)} />
                        ))}
                    </Stack>
                )}

                <EmailLogDetailDialog activity={viewing} onDismiss={() => setViewing(undefined)} />
            </Stack>
        </PanelWrapper>
    );
};

interface IEmailActivityRowProps {
    activity: IEmailActivity;
    onView: () => void;
}

const EmailActivityRow: React.FC<IEmailActivityRowProps> = ({ activity, onView }) => {
    const theme = useTheme();
    const meta = STATUS_META[activity.status ?? ''] ?? { icon: 'Mail', label: activity.status ?? 'Unknown', ok: false };
    const color = meta.ok ? theme.palette.green : theme.palette.redDark;

    const description = (
        <span>
            <Icon iconName={meta.icon} style={{ color, marginRight: '0.5em' }} />
            <strong>{meta.label}</strong>{activity.emailType ? ` — ${activity.emailType}` : ''}
        </span>
    );

    const comments: React.ReactNode[] = [];
    if (activity.subject) comments.push(<div key='subj'>{activity.subject}</div>);
    if (activity.to) comments.push(<div key='to' style={{ color: theme.palette.neutralSecondary }}>To: {activity.to}</div>);
    if (activity.error) comments.push(<div key='err' style={{ color: theme.palette.redDark, wordBreak: 'break-all' }}>{activity.error}</div>);
    comments.push(<Link key='view' onClick={onView}>View details</Link>);

    return (
        <ActivityItem
            activityDescription={description}
            comments={comments}
            timeStamp={activity.timestamp ? new Date(activity.timestamp).toLocaleString() : ''}
            activityIcon={<Icon iconName='Mail' />}
        />
    );
};

const detailBoxStyle: React.CSSProperties = {
    maxHeight: '30vh',
    overflow: 'auto',
    background: '#f3f2f1',
    border: '1px solid #d2d0ce',
    borderRadius: '2px',
    padding: '0.5em',
    margin: 0,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    fontFamily: 'Consolas, Menlo, monospace',
    fontSize: '12px',
};

const DetailRow: React.FC<{ label: string; value?: string }> = ({ label, value }) =>
    value ? (
        <Stack horizontal tokens={{ childrenGap: '0.5em' }}>
            <span style={{ fontWeight: 600, minWidth: 90 }}>{label}</span>
            <span style={{ wordBreak: 'break-all' }}>{value}</span>
        </Stack>
    ) : null;

interface IEmailLogDetailDialogProps {
    activity?: IEmailActivity;
    onDismiss: () => void;
}

/** Detailed view of a single email log entry, mirroring the webhook/extension log detail dialogs. */
const EmailLogDetailDialog: React.FC<IEmailLogDetailDialogProps> = ({ activity, onDismiss }) => (
    <Dialog
        hidden={!activity}
        onDismiss={onDismiss}
        minWidth={560}
        dialogContentProps={{ type: DialogType.largeHeader, title: 'Email Details' }}
    >
        {activity && (
            <Stack tokens={{ childrenGap: '0.5em' }}>
                <DetailRow label='Timestamp' value={activity.timestamp ? new Date(activity.timestamp).toLocaleString() : undefined} />
                <DetailRow label='Type' value={activity.emailType} />
                <DetailRow label='Status' value={activity.status} />
                <DetailRow label='Provider' value={activity.provider} />
                <DetailRow label='To' value={activity.to} />
                <DetailRow label='From' value={activity.from} />
                <DetailRow label='Subject' value={activity.subject} />
                <DetailRow label='Reference' value={activity.referenceId} />
                {activity.error && (
                    <Stack tokens={{ childrenGap: '0.25em' }}>
                        <strong>Error</strong>
                        <pre style={detailBoxStyle}>{activity.error}</pre>
                    </Stack>
                )}
                {activity.serverResponse && (
                    <Stack tokens={{ childrenGap: '0.25em' }}>
                        <strong>SMTP server response</strong>
                        <pre style={detailBoxStyle}>{activity.serverResponse}</pre>
                    </Stack>
                )}
                <Stack horizontalAlign='end'>
                    <DefaultButton text='Close' onClick={onDismiss} />
                </Stack>
            </Stack>
        )}
    </Dialog>
);

export default EmailLogsPanel;
